/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";

import StatTile from "shell/StatTile";
import WeeklyAttendanceChart from "shell/WeeklyAttendanceChart";
import WeeklyTitheWelfareChart from "shell/WeeklyTitheWelfareChart";
import GivingTrendChart from "shell/GivingTrendChart";
import MonthlyTrendChart from "shell/MonthlyTrendChart";
import CategoryDistributionChart from "shell/CategoryDistributionChart";

const sumSeries = (series) => series.reduce((s, d) => s + Number(d.amount || 0), 0);

/**
 * Admin dashboard — chmeetings-style.
 *
 * Sections:
 *   1. Hello hero with church + greeting + sms credit chip
 *   2. 4 stat tiles (members · attendance · offerings · sms balance)
 *   3. Recent attendance / Recent payments side-by-side (placeholder shells)
 */
export default function AdminDashboard() {
  const theme = useTheme();
  const { church, user } = useAuth();
  const [stats, setStats] = useState({
    members: { total: 0 },
    attendance: { today: 0 },
    offerings: { total: 0 },
    pastors: { total: 0, active: 0 },
  });
  const [sms, setSms] = useState({ balance: 0, totalSent: 0, totalCost: 0 });
  const [weeklyAttendance, setWeeklyAttendance] = useState([]);
  const [weeklyTitheWelfare, setWeeklyTitheWelfare] = useState([]);
  const [todayGiving, setTodayGiving] = useState({ tithe: 0, welfare: 0, offering: 0, total: 0 });
  const [givingTrend, setGivingTrend] = useState([]);
  const [monthlyOfferings, setMonthlyOfferings] = useState([]);
  const [monthlyTithes, setMonthlyTithes] = useState([]);
  const [monthlyWelfare, setMonthlyWelfare] = useState([]);
  const [monthlyHarvest, setMonthlyHarvest] = useState([]);
  const [gender, setGender] = useState({ male: 0, female: 0 });

  useEffect(() => {
    if (!church?.id) return;
    const token = localStorage.getItem("authToken");
    const headers = { Accept: "application/json", Authorization: `Bearer ${token}` };

    Promise.all([
      fetch(`${API_BASE}/analytics/members-count?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
      fetch(`${API_BASE}/analytics/attendance-count?church_id=${church.id}`, { headers }).then(
        (r) => r.json()
      ),
      fetch(`${API_BASE}/analytics/offering-count?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
      fetch(`${API_BASE}/analytics/pastors-count?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
    ])
      .then(([m, a, o, p]) =>
        setStats({
          members: { total: m?.total ?? 0 },
          attendance: { today: a?.today ?? 0 },
          offerings: { total: o?.this_month ?? o?.this_week ?? o?.total ?? 0 },
          pastors: { total: p?.total ?? 0, active: p?.active ?? 0 },
        })
      )
      .catch(() => {});

    fetch(`${API_BASE}/analytics/weekly-attendance?church_id=${church.id}`, { headers })
      .then((r) => r.json())
      .then((rows) => setWeeklyAttendance(Array.isArray(rows) ? rows : []))
      .catch(() => {});

    fetch(`${API_BASE}/analytics/weekly-tithe-welfare?church_id=${church.id}`, { headers })
      .then((r) => r.json())
      .then((rows) => setWeeklyTitheWelfare(Array.isArray(rows) ? rows : []))
      .catch(() => {});

    fetch(`${API_BASE}/analytics/today-giving?church_id=${church.id}`, { headers })
      .then((r) => r.json())
      .then((d) =>
        setTodayGiving({
          tithe: d?.tithe ?? 0,
          welfare: d?.welfare ?? 0,
          offering: d?.offering ?? 0,
          total: d?.total ?? 0,
        })
      )
      .catch(() => {});

    fetch(`${API_BASE}/analytics/giving-trend?church_id=${church.id}`, { headers })
      .then((r) => r.json())
      .then((rows) => setGivingTrend(Array.isArray(rows) ? rows : []))
      .catch(() => {});

    Promise.all([
      fetch(`${API_BASE}/analytics/monthly-offerings?church_id=${church.id}`, { headers }).then(
        (r) => r.json()
      ),
      fetch(`${API_BASE}/analytics/monthly-tithes?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
      fetch(`${API_BASE}/analytics/monthly-welfare?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
      fetch(`${API_BASE}/analytics/monthly-harvest?church_id=${church.id}`, { headers }).then((r) =>
        r.json()
      ),
    ])
      .then(([o, t, w, h]) => {
        setMonthlyOfferings(Array.isArray(o) ? o : []);
        setMonthlyTithes(Array.isArray(t) ? t : []);
        setMonthlyWelfare(Array.isArray(w) ? w : []);
        setMonthlyHarvest(Array.isArray(h) ? h : []);
      })
      .catch(() => {});

    fetch(`${API_BASE}/v3/sms/balance?church_id=${church.id}`, { headers })
      .then((r) => r.json())
      .then((j) => {
        const d = j?.data || {};
        setSms({
          balance: d.balance ?? 0,
          totalSent: d.total_sent ?? 0,
          totalCost: d.total_spent ?? 0,
        });
      })
      .catch(() => {});

    fetch(`${API_BASE}/analytics/demographics?church_id=${church.id}`, { headers })
      .then((r) => r.json())
      .then((d) => setGender({ male: d?.gender?.male ?? 0, female: d?.gender?.female ?? 0 }))
      .catch(() => {});
  }, [church?.id]);

  const greeting = greetingFor(new Date());

  return (
    <Box>
      {/* Hero */}
      <Card
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3.5 },
          background: `linear-gradient(135deg, ${theme.palette.teal[600]} 0%, ${theme.palette.teal[400]} 100%)`,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: -40,
            top: -40,
            width: 220,
            height: 220,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <Typography variant="overline" sx={{ opacity: 0.9, color: "white" }}>
          {greeting}, {firstName(user?.name)}
        </Typography>
        <Typography variant="h2" sx={{ color: "white", mt: 0.5 }}>
          {church?.name || "Your Church"}
        </Typography>
        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <Chip
            icon={<Icon sx={{ color: "white !important", fontSize: 16 }}>group</Icon>}
            label={`${stats.members.total.toLocaleString()} members`}
            sx={pillSx}
          />
          <Chip
            icon={<Icon sx={{ color: "white !important", fontSize: 16 }}>how_to_reg</Icon>}
            label={`${stats.attendance.today} present today`}
            sx={pillSx}
          />
          <Chip
            icon={<Icon sx={{ color: "white !important", fontSize: 16 }}>sms</Icon>}
            label={`${Number(sms.balance).toLocaleString()} SMS credits`}
            sx={pillSx}
          />
        </Box>
      </Card>

      {/* Stat tiles */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="group"
            label="Total Members"
            value={stats.members.total.toLocaleString()}
            sub="across all departments"
            tone="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="how_to_reg"
            label="Attendance today"
            value={stats.attendance.today.toLocaleString()}
            sub="checked-in this service"
            tone="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="payments"
            label="This month's offerings"
            value={`GHS ${Number(stats.offerings.total).toLocaleString()}`}
            sub="tithes + welfare + offering"
            tone="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="sms"
            label="SMS Credits"
            value={Number(sms.balance).toLocaleString()}
            sub={`${sms.totalSent.toLocaleString()} sent · GHS ${Number(sms.totalCost).toFixed(2)}`}
            tone={sms.balance < 50 ? "warning" : "purple"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="man"
            label="Men"
            value={gender.male.toLocaleString()}
            sub="male members"
            tone="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatTile
            icon="woman"
            label="Women"
            value={gender.female.toLocaleString()}
            sub="female members"
            tone="success"
          />
        </Grid>
      </Grid>

      {/* Today's giving */}
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Today&apos;s Giving
      </Typography>
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatTile
            icon="volunteer_activism"
            label="Today's Tithe"
            value={`GHS ${Number(todayGiving.tithe).toLocaleString()}`}
            sub="confirmed payments today"
            tone="primary"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatTile
            icon="health_and_safety"
            label="Today's Welfare"
            value={`GHS ${Number(todayGiving.welfare).toLocaleString()}`}
            sub="confirmed payments today"
            tone="info"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatTile
            icon="payments"
            label="Today's Offering"
            value={`GHS ${Number(todayGiving.offering).toLocaleString()}`}
            sub="confirmed payments today"
            tone="success"
          />
        </Grid>
      </Grid>

      {/* Giving trend line chart */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12}>
          <Card sx={{ p: { xs: 2, md: 3 } }}>
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h5">Giving Trend</Typography>
              <Chip
                size="small"
                label="Last 30 days"
                sx={{ bgcolor: theme.palette.ink[100], color: "text.secondary" }}
              />
            </Box>
            <GivingTrendChart data={givingTrend} />
          </Card>
        </Grid>
      </Grid>

      {/* Monthly breakdown */}
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Monthly Breakdown
      </Typography>
      <Grid container spacing={2.5} sx={{ mb: 1 }}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Monthly Offerings Trend
            </Typography>
            <MonthlyTrendChart
              data={monthlyOfferings}
              color={theme.palette.teal[500]}
              emptyLabel="No offerings recorded yet."
            />
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Monthly Tithes Trend
            </Typography>
            <MonthlyTrendChart
              data={monthlyTithes}
              color={theme.palette.success.main}
              emptyLabel="No tithes recorded yet."
            />
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Monthly Welfare Trend
            </Typography>
            <MonthlyTrendChart
              data={monthlyWelfare}
              color={theme.palette.info.main}
              emptyLabel="No welfare payments recorded yet."
            />
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Monthly Harvest Trend
            </Typography>
            <MonthlyTrendChart
              data={monthlyHarvest}
              color={theme.palette.warning.main}
              emptyLabel="No harvest/seed payments recorded yet."
            />
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Category Distribution (last 12 months)
            </Typography>
            <CategoryDistributionChart
              labels={["Offerings", "Tithes", "Welfare", "Harvest"]}
              data={[
                sumSeries(monthlyOfferings),
                sumSeries(monthlyTithes),
                sumSeries(monthlyWelfare),
                sumSeries(monthlyHarvest),
              ]}
              colors={[
                theme.palette.teal[500],
                theme.palette.success.main,
                theme.palette.info.main,
                theme.palette.warning.main,
              ]}
            />
          </Card>
        </Grid>
      </Grid>

      {/* Two-column secondary section */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7}>
          <Card sx={{ p: 3, height: "100%" }}>
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h5">Weekly attendance</Typography>
              <Chip
                size="small"
                label="Last 7 days"
                sx={{ bgcolor: theme.palette.ink[100], color: "text.secondary" }}
              />
            </Box>
            <WeeklyAttendanceChart data={weeklyAttendance} />
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card sx={{ p: 3, height: "100%" }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Pastors
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Roster summary
            </Typography>
            <Box sx={{ display: "flex", gap: 3, mt: 2 }}>
              <Box>
                <Typography variant="h2" sx={{ lineHeight: 1, fontSize: 36 }}>
                  {stats.pastors.total}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="h2"
                  sx={{ lineHeight: 1, fontSize: 36, color: "primary.main" }}
                >
                  {stats.pastors.active}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Active
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2.5} sx={{ mt: 2.5 }}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h5">Tithe &amp; Welfare</Typography>
              <Chip
                size="small"
                label="Last 7 days"
                sx={{ bgcolor: theme.palette.ink[100], color: "text.secondary" }}
              />
            </Box>
            <WeeklyTitheWelfareChart data={weeklyTitheWelfare} />
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

const pillSx = {
  bgcolor: "rgba(255,255,255,0.18)",
  color: "white",
  fontWeight: 600,
  border: "1px solid rgba(255,255,255,0.18)",
  "& .MuiChip-icon": { ml: 0.5 },
};

function greetingFor(d) {
  const h = d.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
function firstName(name) {
  return (name || "Admin").split(" ")[0];
}
